import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useConfirm } from 'material-ui-confirm';
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import Grid from 'material/components/Grid';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

import FormControl from 'material/components/FormControl';
import Select from 'material/components/Select';
import MuiMenuItem from 'material/components/MenuItem';

import { fetchRootList } from 'store/actions/contacts';
import {
  deleteCustomField,
  fetchCustomFields,
  patchCustomField,
  postCustomField,
} from 'store/actions/customFields';
import { contactSelectors } from 'store/selectors/contacts';
import { customFieldsSelectors } from 'store/selectors/customFields';
import { setSnackbarError } from 'store/actions/global';

import { BreadcrumbsNavigation, Container } from 'common/components';
import TopCard from 'common/containers/TopCard';
import { CardWrapper, CircularProgressWrapper } from 'globalStyles';
import { fieldTypes } from 'utils/constants';
import { isEven } from 'utils/helpers';
import { colors } from 'themes';

import CustomFieldDialog from '../components/CustomFieldDialog';

const ActionButton = styled(Button)`
  padding: 6px 16px;
`;

const FilterToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 16px;
`;

const { REACT_APP_FEATURE_FLAG_MULTIPLE_CHOICE } = process.env;

const fieldTypeLabel = fieldType => {
  switch (fieldType) {
    case fieldTypes.textField:
      return 'Text Field';
    case fieldTypes.singleChoiceField:
      return 'Single Choice';
    case fieldTypes.multipleChoiceField:
      return 'Multi-select Choice';
    default:
      return '';
  }
};

const CustomFields = () => {
  const { organizationUuid } = useParams();
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const breadcrumbs = [
    {
      link: `/${organizationUuid}/contacts/lists`,
      name: 'Lists',
    },
    {
      link: '',
      name: 'Custom Fields',
    },
  ];

  const [fieldTypeFilter, setFieldTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('label');
  const [sortDirection, setSortDirection] = useState('asc');

  const defaultCustomFieldState = {
    description: '',
    fieldType: '',
    isModalOpened: false,
    label: '',
    placeholder: '',
  };
  const [editedCustomField, setEditedCustomField] = useState({ ...defaultCustomFieldState });

  const { customFieldsList, isFetchingCustomFields, isPendingCustomField, rootList } = useSelector(
    state => ({
      customFieldsList: customFieldsSelectors.getCustomFieldsListData(state) || [],
      isFetchingCustomFields: customFieldsSelectors.isFetchingCustomFieldsListData(state),
      isPendingCustomField: customFieldsSelectors.isPendingCustomField(state),
      rootList: contactSelectors.getOrganizationRootList(state)[organizationUuid] || {},
    })
  );

  const { multipleChoiceField, textField, singleChoiceField } = fieldTypes;

  useEffect(() => {
    if (Object.keys(rootList).length === 0) {
      dispatch(fetchRootList(organizationUuid)).then(data =>
        dispatch(fetchCustomFields(data.uuid))
      );
    } else {
      dispatch(fetchCustomFields(rootList.uuid));
    }
  }, []);

  const handleUpdateCustomField = async () => {
    const params = {
      description: editedCustomField.description,
      fieldType: editedCustomField.fieldType,
      label: editedCustomField.label,
      ...(editedCustomField.fieldType === textField
        ? {
            placeholder: editedCustomField.placeholder,
          }
        : {}),
      ...(editedCustomField.fieldType !== textField
        ? {
            options: editedCustomField.options.map(option => option.value),
          }
        : {}),
    };

    try {
      if (!editedCustomField.uuid) {
        await dispatch(postCustomField(rootList.uuid, params));
      } else {
        await dispatch(patchCustomField(rootList.uuid, editedCustomField.uuid, params));
      }
      dispatch(fetchCustomFields(rootList.uuid));
      setEditedCustomField({ ...defaultCustomFieldState });
    } catch (error) {
      dispatch(setSnackbarError());
    }
  };

  const handleDialogClose = () => {
    setEditedCustomField({ ...defaultCustomFieldState });
  };

  const handleDeleteCustomField = customField => {
    confirm({
      confirmationText: 'Delete',
      title: 'Delete this custom field?',
    }).then(() => dispatch(deleteCustomField(customField.staticList, customField.uuid)));
  };

  const handleSort = column => {
    if (sortBy === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const filteredFields =
    fieldTypeFilter === 'all'
      ? customFieldsList
      : customFieldsList.filter(field => field.fieldType === fieldTypeFilter);

  const sortedFields = [...filteredFields].sort((a, b) => {
    const aVal = sortBy === 'label' ? a.label : fieldTypeLabel(a.fieldType);
    const bVal = sortBy === 'label' ? b.label : fieldTypeLabel(b.fieldType);

    return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  const headerCellSx = {
    color: colors.osloGray,
    fontSize: '14px',
    fontWeight: 500,
  };

  return (
    <>
      <BreadcrumbsNavigation links={breadcrumbs} />
      <Container>
        <Grid container>
          <TopCard
            title="Custom Fields"
            restActions={
              <ActionButton
                color="secondary"
                disableElevation
                onClick={() =>
                  setEditedCustomField({
                    ...defaultCustomFieldState,
                    isModalOpened: true,
                  })
                }
                startIcon={<AddIcon />}
                variant="contained"
              >
                New Field
              </ActionButton>
            }
          />
        </Grid>
        <CardWrapper>
          <FilterToolbar>
            <FormControl sx={{ minWidth: 140 }} size="small">
              <Select
                inputProps={{ 'aria-label': 'Filter field type' }}
                onChange={event => setFieldTypeFilter(event.target.value)}
                value={fieldTypeFilter}
                variant="outlined"
              >
                <MuiMenuItem value="all">All Fields</MuiMenuItem>
                <MuiMenuItem
                  disabled={!customFieldsList.find(f => f.fieldType === textField)}
                  value={textField}
                >
                  Text Fields
                </MuiMenuItem>
                <MuiMenuItem
                  disabled={!customFieldsList.find(f => f.fieldType === singleChoiceField)}
                  value={singleChoiceField}
                >
                  Single Choice
                </MuiMenuItem>
                {REACT_APP_FEATURE_FLAG_MULTIPLE_CHOICE && (
                  <MuiMenuItem
                    disabled={!customFieldsList.find(f => f.fieldType === multipleChoiceField)}
                    value={multipleChoiceField}
                  >
                    Multi-select Choice
                  </MuiMenuItem>
                )}
              </Select>
            </FormControl>
          </FilterToolbar>
          {isFetchingCustomFields ? (
            <CircularProgressWrapper>
              <CircularProgress />
            </CircularProgressWrapper>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>
                    <TableSortLabel
                      active={sortBy === 'label'}
                      direction={sortBy === 'label' ? sortDirection : 'asc'}
                      onClick={() => handleSort('label')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    <TableSortLabel
                      active={sortBy === 'fieldType'}
                      direction={sortBy === 'fieldType' ? sortDirection : 'asc'}
                      onClick={() => handleSort('fieldType')}
                    >
                      Field type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFields.map((customField, index) => (
                  <TableRow
                    key={customField.uuid}
                    sx={{ backgroundColor: isEven(index) ? colors.alabaster : colors.white }}
                  >
                    <TableCell sx={{ fontWeight: 'bold' }}>{customField.label}</TableCell>
                    <TableCell>{fieldTypeLabel(customField.fieldType)}</TableCell>
                    <TableCell align="right">
                      <Button
                        onClick={() =>
                          setEditedCustomField({
                            ...customField,
                            isModalOpened: true,
                            options: customField.options.map(option => ({
                              label: option,
                              value: option,
                            })),
                          })
                        }
                        startIcon={<EditIcon />}
                      >
                        Edit
                      </Button>
                      <Button
                        color="error"
                        onClick={() => handleDeleteCustomField(customField)}
                        startIcon={<DeleteOutlineIcon />}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardWrapper>
      </Container>
      <CustomFieldDialog
        editedCustomField={editedCustomField}
        handleDialogClose={handleDialogClose}
        handleUpdateCustomField={handleUpdateCustomField}
        isPendingCustomField={isPendingCustomField}
        setEditedCustomField={setEditedCustomField}
      />
    </>
  );
};

export default CustomFields;
